import { useUser } from '~/stores/useUser'
import type { FinanceMonth, FinanceMonthData } from '~/utils/financeMonths'
import type { BusinessMetrics, PaymentSourceFilter } from '~/utils/businessMetrics'
import type { MacOSReleaseMetadata, MacOSReleaseTarget, MacOSUpdateState } from '~/utils/macosUpdate'
import type { OpenWrtReleaseInput, OpenWrtReleaseTarget, OpenWrtUpdateState, OpenWrtVersion } from '~/utils/openwrtUpdate'
import type { AutostatStatus } from '~/utils/autostat'
import type { LatestReleaseSource, LatestReleases } from '~/utils/clientLatestVersion'
import { APPLE_ACCOUNT, relatedAppleAccountUsers } from '~/utils/appleAccount'

export type PlanPricingBasicPolicy = {
  relay: {
    price_eur_cents_per_gb: number
    min_purchase_gb: number
    period_purchase_limit_gb: number
  }
}

export type PlanPricingProPolicy = {
  standard: {
    kind: 'packages'
    packages: Array<{
      quota_gb: number
      price_eur_cents: number
    }>
  }
  relay: PlanPricingBasicPolicy['relay'] & {
    full_quota_gb: number
  }
}

export type PlanPricingHomePolicy = {
  standard: {
    kind: 'per_gb'
    price_eur_cents_per_gb: number
    min_purchase_gb: number
    max_purchase_gb: number
  }
}

export type PlanPricingResponse = {
  plans: Array<{
    plan_id: 2 | 4 | 5 | 6
    plan_name: string
    updated_at: string
    data_addon_policy: PlanPricingBasicPolicy | PlanPricingProPolicy | PlanPricingHomePolicy
  }>
}

export type PlanPricingUpdateRequest = {
  plans: Array<{
    plan_id: 2 | 4 | 5 | 6
    expected_updated_at: string
    data_addon_policy: PlanPricingBasicPolicy | PlanPricingProPolicy | PlanPricingHomePolicy
  }>
}

export type BigNewsAudienceMode = 'broadcast' | 'users' | 'devices'

export type BigNewsCampaignRequest = {
  title: string
  title_en?: string
  html: string
  html_en?: string
  push_body?: string
  audience: {
    mode: BigNewsAudienceMode
    user_ids?: number[]
    device_uuids?: string[]
  }
  send_push: boolean
  preview_signature?: string
}

export type BigNewsCampaignPreview = {
  audience: {
    mode: BigNewsAudienceMode
    user_ids: number[]
    device_uuids: string[]
  }
  matched_user_ids: number[]
  target_devices: Array<{
    id?: number
    user_id: number
    device_uuid: string
    device_name?: string | null
    platform: string
    push_enabled: boolean
  }>
  unmatched_user_ids: number[]
  unmatched_device_uuids: string[]
  ambiguous_device_uuids: string[]
  push: {
    eligible_device_count: number
    opted_out_device_count: number
    platform_counts: Record<'ios' | 'android', number>
  }
  preview_signature: string
}

interface PmwPayoutDetailsResponse {
  id: string
  summary: {
    paidGross: number
    transferFee: number
    paidNet: number
    currency: string
    datePaid: string
    paymentMethod: string
  }
  rows: Array<{
    txId: string
    txDate: string
    type: string
    project: string
    userId: string
    country: string
    paymentSystem: string
    processingCurrency: string
    endUserPayment: number
    payoutCurrency: string
    revenue: number
    rollingReserve: number
    commission: number
  }>
  fetchedAt: string
}

declare global {
  type IPLocation = {
    countryName?: string
    regionName?: string
    cityName?: string
    // ipip only (actually data is from qqwry)
    ispDomain?: string
    // latitude?: string
    // longitude?: string
    // timezone?: string
    // utcOffset?: string
    // chinaAdminCode?: string
    // iddCode?: string
    // countryCode?: string
    // continentCode?: string
    // idc?: string
    // baseStation?: string
    // countryCode3?: string
    // europeanUnion?: string
    // currencyCode?: string
    // currencyName?: string
    // anycast?: string
    // route?: string
    // asn?: string
    // usage_type?: string
    // district_info?: string
    // asn_info?: string
    // extra
    internal?: boolean
  }

  type IPToLocationMapping = {
    [key: string]: IPLocation
  }

  type CacheOperationGeneralResponse = {
    command: unknown[][]
    error: string | null,
    result: [error: Error | null, result: unknown][] | null,
  }

  type AppleTestFlightGroupStatus = {
    id: string
    label: string
    testerCount: number
    latestBuilds: Array<{ platform: 'IOS' | 'TV_OS', version: string }>
  }

  type AppleTestFlightBuildStatus = {
    id: string
    version: string
    marketingVersion: string
    uploadedDate: string
    processingState: string
    expired: boolean
    minOsVersion: string
    internalBuildState: string | null
    externalBuildState: string | null
    betaReviewState: string | null
    betaReviewSubmittedDate: string | null
    groups: string[]
  }

  type AppleTestFlightAppStatus = {
    id: string
    name: string
    error: string | null
    testerSlotsUsed: number | null
    testerSlotLimit: number
    groups: AppleTestFlightGroupStatus[]
    platforms: Array<{ platform: 'IOS' | 'TV_OS', builds: AppleTestFlightBuildStatus[] }>
  }

  type AppleTestFlightStatusResponse = {
    fetchedAt: string
    apps: AppleTestFlightAppStatus[]
  }
}

export const useAdmin = {
  suspendAccount(opt: any) {
    return useAPI().post(`admin/suspend_account`, { body: opt }) as Promise<{result: {buy_suspend: boolean, suspend: boolean, tk_suspend: boolean}}>
  },
  allowSea(opt: any) {
    return useAPI().post(`admin/allow_sea`, { body: opt }) as Promise<{allow_sea: boolean}>
  },
  setClientFeatureFlag<T extends 'allowEditClashDefaults' | 'allowSetHy2Bandwidth' | 'allowCoreEngineSelection' | 'allowManageExternalBinaries' | 'allowHomeTunMode' | 'allowDisableGfwMrs' | 'allowNaiveConfigOptions' | 'allowSpeedTest'>(opt: { user_id: number; flag: T; allow: boolean }) {
    return useAPI().post(`admin/client_feature_flag`, { body: opt }) as Promise<{ flag: T; value: boolean }>
  },
  blockUserPayment(opt: any) {
    return useAPI().post(`admin/block_user_payment`, { body: opt })
  },
  unblockUserPayment(opt: any) {
    return useAPI().post(`admin/unblock_user_payment`, { body: opt })
  },
  checkBlockedPayer(opt: {userId: number}): Promise<CheckedBlockedPayerRecs> {
    return useAPI().get(`admin/check_blocked_payer`, { params: opt })
  },
  blockUserPayPalPayer(opt: any) {
    return useAPI().post('admin/block_user_paypal_payer', { body: opt })
  },
  unblockUserPayPalPayer(opt: any) {
    return useAPI().post(`admin/unblock_user_paypal_payer`, { body: opt })
  },
  blockUserDevice(opt: any) {
    return useAPI().post(`admin/block_user_device`, { body: opt })
  },
  unblockUserDevice(opt: any) {
    return useAPI().post(`admin/unblock_user_device`, { body: opt })
  },
  addData(opt: any) {
    return useAPI().post(`admin/addquota`, { body: opt }) as Promise<{result: {status: string, pre: number, nor: number}}>
  },
  setCustomMonthlyQuote(opt: {
    serviceId: number
    customMonthlyQuote: number | null
    customMonthlyPreQuota: number | null
  }) {
    return useAPI().put(`admin/services/${opt.serviceId}/custom_monthly_quote`, {
      body: {
        custom_monthly_quote: opt.customMonthlyQuote,
        custom_monthly_pre_quota: opt.customMonthlyPreQuota,
      },
    }) as Promise<{
      result: {
        service_id: number
        custom_monthly_quote: number | null
        custom_monthly_pre_quota: number | null
      }
    }>
  },
  getRelayDataAddonState(opt: { serviceId: number }) {
    return useAPI().get(`admin/services/${opt.serviceId}/relay_data_addon_state`) as Promise<{
      result: {
        service_id: number
        period_key: string
        purchased_gb: number
        all_pre: number
        buy_length: number
        period_purchase_limit_gb: number
        all_pre_limit: number
      }
    }>
  },
  updateRelayDataAddonState(opt: {
    serviceId: number
    purchasedGb: number
    allPre: number
  }) {
    return useAPI().put(`admin/services/${opt.serviceId}/relay_data_addon_state`, {
      body: {
        purchased_gb: opt.purchasedGb,
        all_pre: opt.allPre,
      },
    }) as Promise<{
      result: {
        service_id: number
        period_key: string
        purchased_gb: number
        all_pre: number
        buy_length: number
        period_purchase_limit_gb: number
        all_pre_limit: number
      }
    }>
  },
  movePreToStandard(opt: { s_id: number; dry_run?: boolean }) {
    return useAPI().post(`admin/move_pre_to_standard`, { body: opt }) as Promise<{
      result: {
        applied: boolean
        before: { quote: number; pre_quota: number; pre_used: number }
        after?: { quote: number; pre_quota: number; pre_used: number }
        remaining: number
        reason?: 'noRelayQuota' | 'unlimitedStandard'
        // commit only
        status?: string
        turned_on?: boolean
      }
    }>
  },
  delService(opt: any) {
    return useAPI().post(`admin/delservice`, { body: opt })
  },
  stopServiceCurrentMonth(opt: { service_id: number }) {
    return useAPI().post(`admin/stop_service_current_month`, { body: opt })
  },
  shadowBanService(opt: any) {
    return useAPI().post(`admin/shadow_ban_service`, { body: opt }) as Promise<ServiceExtra>
  },
  addMoney(opt: { user_id: number, amount: number, is_payment: boolean, reason?: string, do_not_record: boolean, currency: string}) {
    return useAPI().post(`admin/addmoney`, { body: opt }) as Promise<{result: {money: number}}>
  },
  getOverLimitRecords(opt: any) {
    return useAPI().post(`admin/over_limit`, { body: opt })
  },
  getOverLimitServiceInfo(opt: any) {
    return useAPI().post(`admin/over_limit_service_info`, { body: opt })
  },
  getOverLimitWarningHistory(opt: any) {
    return useAPI().post(`admin/over_limit_warning`, { body: opt })
  },
  sendOverLimitWarning(opt: any) {
    return useAPI().post(`admin/send_over_limit_warning`, { body: opt })
  },
  getAllAPServers(opt: any) {
    return useAPI().get(`admin/all_ap_servers`, { params: opt }) as Promise<string[]>
  },
  getRealTimeSpeed(opt: any) {
    return useAPI().get(`funstuff/real_time_speed`)
  },
  getEmailRender(opt: any) {
    return useAPI().get(`funstuff/md_render`, { params: opt }) as Promise<string>
  },
  sendEmail(opt: any) {
    return useAPI().post(`admin/send_email`, { body: opt }) as Promise<{result: any, success: boolean}>
  },
  useDynamicPMWUserId(opt: any) {
    return useAPI().post(`admin/dynamic_pmw_user_id`, { body: opt })
  },
  delRealTimeSpeedServer(opt: any) {
    return useAPI().put(`funstuff/real_time_speed`, { body: opt })
  },
  getAlibabaTrans(opt: any) {
    return useAPI().get(`funstuff/get_alibabacloud_trans`, { params: opt })
  },
  getServerStatSpeed(opt: any) {
    return useAPI().get(`admin/servertrspeed`, { params: opt }) as Promise<any>
  },
  getServerList(opt: any) {
    return useAPI().get(`admin/server_list`, { params: opt }) as Promise<serversAttributes[]>
  },
  getDownServers(opt: any) {
    return useAPI().get(`admin/downservers`, { params: opt }) as Promise<{result: any}>
  },
  getFailedServers(opt: any) {
    return useAPI().get(`admin/failedservers`, { params: opt }) as Promise<{result: any}>
  },
  getStatLastPost({ type, params }: { type: string, params: any }) {
    return useAPI().get(`admin/${type}_stat_last_post`, { params })
  },
  delFailedServers(opt: any) {
    return useAPI().put(`admin/failedservers`, { body: opt })
  },
  setDownServers(opt: any) {
    return useAPI().post(`admin/downservers`, { body: opt })
  },
  setDLClientVersion(opt: any) {
    console.log('setDLClientVersion', { body: opt })
    return useAPI().post(`admin/client_version`, { body: opt })
  },
  getAndroidUpdateVersions() {
    return useAPI().get(`admin/android-update/versions`) as Promise<{
      channels: {
        stable: client_versionsAttributes | null
        beta: client_versionsAttributes | null
      }
      downloads: {
        tutorial: client_versionsAttributes | null
      }
      versions: client_versionsAttributes[]
    }>
  },
  getMacOSUpdateVersions() {
    return useAPI().get('admin/macos-update/versions') as Promise<MacOSUpdateState>
  },
  createMacOSUpdateUploadUrl(opt: MacOSReleaseMetadata) {
    return useAPI().post('admin/macos-update/upload-url', { body: opt }) as Promise<{
      key: string, uploadUrl: string, publicUrl: string, contentType: string
    }>
  },
  registerMacOSUpdateVersion(opt: MacOSReleaseMetadata & {
    key: string, fileName: string, minVersionCode: number, releaseNotes: string, releaseNotesEn: string
  }) {
    return useAPI().post('admin/macos-update/version', { body: opt }) as Promise<{ state: MacOSUpdateState }>
  },
  promoteMacOSUpdateVersion(opt: { target: MacOSReleaseTarget, versionCode: number }) {
    return useAPI().post('admin/macos-update/promote', { body: opt }) as Promise<{ state: MacOSUpdateState }>
  },
  unpromoteMacOSUpdateVersion(opt: { target: MacOSReleaseTarget }) {
    return useAPI().delete('admin/macos-update/promote', { body: opt }) as Promise<{ state: MacOSUpdateState }>
  },
  getClientLatestReleases() {
    return useAPI().get('admin/client-latest-releases') as Promise<Record<LatestReleaseSource, LatestReleases | { error: string }>>
  },
  getAppleTestFlightStatus(opt: { refresh?: boolean } = {}) {
    return useAPI().get('admin/apple-app/testflight-status', { query: opt.refresh ? { refresh: '1' } : undefined }) as Promise<AppleTestFlightStatusResponse>
  },
  getExternalPurchaseLinkStorefronts() {
    return useAPI().get('admin/external-purchase-links') as Promise<{ storefronts: string[] }>
  },
  saveExternalPurchaseLinkStorefronts(storefronts: string[]) {
    return useAPI().put('admin/external-purchase-links', { body: { storefronts } }) as Promise<{ storefronts: string[] }>
  },
  createAndroidUpdateUploadUrl(opt: {
    versionName: string
    versionCode: number
    fileName: string
    contentType: string
  }) {
    return useAPI().post(`admin/android-update/upload-url`, { body: opt }) as Promise<{
      bucket: string
      key: string
      uploadUrl: string
      publicUrl: string
      contentType: string
    }>
  },
  registerAndroidUpdateVersion(opt: any) {
    return useAPI().post(`admin/android-update/version`, { body: opt }) as Promise<{
      version: client_versionsAttributes
      state: {
        channels: {
          stable: client_versionsAttributes | null
          beta: client_versionsAttributes | null
        }
        downloads: {
          tutorial: client_versionsAttributes | null
        }
        versions: client_versionsAttributes[]
      }
    }>
  },
  promoteAndroidUpdateVersion(opt: { target: 'stable' | 'beta' | 'tutorial'; versionCode: number }) {
    return useAPI().post(`admin/android-update/promote`, { body: opt }) as Promise<{
      promoted: {
        target: 'stable' | 'beta' | 'tutorial'
        version: client_versionsAttributes
      }
      state: {
        channels: {
          stable: client_versionsAttributes | null
          beta: client_versionsAttributes | null
        }
        downloads: {
          tutorial: client_versionsAttributes | null
        }
        versions: client_versionsAttributes[]
      }
    }>
  },
  unpromoteAndroidUpdateVersion(opt: { target: 'stable' | 'beta' | 'tutorial' }) {
    return useAPI().delete(`admin/android-update/promote`, { body: opt }) as Promise<{
      removed: {
        target: 'stable' | 'beta' | 'tutorial'
        version: client_versionsAttributes | null
      }
      state: {
        channels: {
          stable: client_versionsAttributes | null
          beta: client_versionsAttributes | null
        }
        downloads: {
          tutorial: client_versionsAttributes | null
        }
        versions: client_versionsAttributes[]
      }
    }>
  },
  getOpenWrtUpdateVersions() {
    return useAPI().get('admin/openwrt-update/versions') as Promise<OpenWrtUpdateState>
  },
  createOpenWrtUpdateUploadUrl(opt: { versionName: string, versionCode: number, fileName: string, sha256: string }) {
    return useAPI().post('admin/openwrt-update/upload-url', { body: opt }) as Promise<{
      key: string, bucket: string, uploadUrl: string, publicUrl: string, contentType: string
    }>
  },
  registerOpenWrtUpdateVersion(opt: OpenWrtReleaseInput) {
    return useAPI().post('admin/openwrt-update/version', { body: opt }) as Promise<{ version: OpenWrtVersion, state: OpenWrtUpdateState }>
  },
  promoteOpenWrtUpdateVersion(opt: { target: OpenWrtReleaseTarget, versionCode: number }) {
    return useAPI().post('admin/openwrt-update/promote', { body: opt }) as Promise<{ state: OpenWrtUpdateState }>
  },
  unpromoteOpenWrtUpdateVersion(opt: { target: OpenWrtReleaseTarget }) {
    return useAPI().delete('admin/openwrt-update/promote', { body: opt }) as Promise<{ state: OpenWrtUpdateState }>
  },
  callAdmin(opt: any) {
    return useAPI().get(`admin/call_admin`, { params: opt }) as Promise<{result: any}>
  },
  switchDoNotCall(opt: any) {
    return useAPI().post(`admin/do_not_call`, { body: opt })
  },
  purgeEmailAndConfCache(opt: any) {
    return useAPI().delete(`admin/clear_email_file_cache`, opt)
  },
  setDownReason(opt: any) {
    return useAPI().post(`admin/status_reason`, { body: opt })
  },
  updateMonitor(opt: any) {
    return useAPI().post(`admin/update_monitor_server_list`, { body: opt })
  },
  setDomainAndDelimiter(opt: any) {
    return useAPI().post(`admin/client_site_url`, { body: opt }) as Promise<{arr: string | string[], delimiter: string | string[] }>
  },
  getServerSpeedRecords(opt: any) {
    return useAPI().get(`funstuff/server_speed_records`, { params: opt })
  },
  getClientHeartBeatRecords(opt: any) {
    return useAPI().get(`client/heart_beat`, { params: opt })
  },
  remoteExecute(opt: any) {
    return useAPI().post(`admin/remote_execute`, { body: opt })
  },
  writeFile(opt: any) {
    return useAPI().post(`admin/write_file_to_server`, { body: opt })
  },
  getTrAbuser(opt: any) {
    return useAPI().get(`admin/tr_abuser`, { params: opt }) as Promise<trAbuserServicesAttributes[]>
  },
  getDeviceAbusers(opt: { duration?: number; minDeviceCount?: number; minIpCount?: number }) {
    return useAPI().get(`admin/device_abuser`, { params: opt })
  },
  getDeviceAbuserUserMeta(opt: { userIdArray: number[] }) {
    return useAPI().post(`admin/device_abuser_user_meta`, { body: opt })
  },
  getDeviceAbuserAi(opt: { items: any[]; durationDays?: number; limit?: number }) {
    return useAPI().post(`admin/device_abuser_ai`, { body: opt })
  },
  getTrAbuserWarningRecords(opt: any) {
    return useAPI().post(`admin/tr_abuser_warning_records`, { body: opt })
  },
  sendTrAbuseWarning(opt: any) {
    return useAPI().post(`admin/send_tr_abuse_warning`, { body: opt })
  },
  getTrAbuseNotes(opt: any) {
    return useAPI().post(`admin/get_tr_abuse_notes`, { body: opt }) as Promise<Note[]>
  },
  addTrAbuseNote(opt: any) {
    return useAPI().post(`admin/tr_abuse_notes`, { body: opt }) as Promise<Note>
  },
  delTrAbuseNote(opt: any) {
    return useAPI().delete(`admin/tr_abuse_notes/${opt.noteId}`, opt)
  },
  fetchAccessLog(opt: any) {
    return useAPI().get(`admin/download_access_log`, { params: opt })
  },
  getOssAccessLogs(opt: { requestIds: string[]; from?: string; to?: string; limit?: number }) {
    return useAPI().post(`admin/oss_access_logs`, { body: opt }) as Promise<{ logs: Record<string, any> }>
  },
  getIPGeo(opt: any): Promise<IPToLocationMapping> {
    return useAPI().post(`funstuff/ipgeo`, { body: opt }) as Promise<IPToLocationMapping>
  },
  getNFStatus(opt: any) {
    return useAPI().get(`nf_status`, { params: opt }) as Promise<NFStatus[]>
  },
  deleteNFStatus(serverName: string) {
    return useAPI().delete(`nf_status/${serverName}`, {})
  },
  getGeminiStatus(opt: any) {
    return useAPI().get(`gemini_status`, { params: opt }) as Promise<GeminiServerStatus[]>
  },
  getGeminiLines() {
    return useAPI().get(`gemini_status/lines`, {}) as Promise<GeminiLineStatus[]>
  },
  deleteGeminiStatus(serverName: string) {
    return useAPI().delete(`gemini_status/${serverName}`, {})
  },
  getPushLog({ logId, params }: { logId: string; params: any }) {
    return useAPI().get(`admin/error_logs/${logId}`, { params })
  },
  deleteUser(opt: any) {
    return useAPI().post(`admin/delete_user`, { body: opt })
  },
  async setAllowedPaymentMethod({ userId, allowedPaymentMethods }: { userId: number; allowedPaymentMethods: string[] }) {
    const data = await useAPI().post(`admin/allowed_payment_methods`, {
      body: {
        user_id: useUser().id,
        allowed_payment_methods: allowedPaymentMethods,
      }
    })
    useUser().updateExtra(
      {
        allowed_payment_methods: allowedPaymentMethods
      }
    )
    return data
  },
  async setDisabledPaymentMethod({ userId, disabledPaymentMethods }: { userId: number; disabledPaymentMethods: string[] }) {
    const data = await useAPI().post(`admin/disabled_payment_methods`, {
      body: {
        user_id: useUser().id,
        disabled_payment_methods: disabledPaymentMethods,
      }
    })
    useUser().updateExtra(
      {
        disabled_payment_methods: disabledPaymentMethods
      }
    )
    return data
  },
  async getFinanceMonth(params: FinanceMonth): Promise<FinanceMonthData> {
    return await useAPI().get('finance/month', { params, timeout: 35_000, retry: 0 }) as FinanceMonthData
  },
  async getBusinessMetrics(params: { days: number, endDate: string, source: PaymentSourceFilter }): Promise<BusinessMetrics> {
    return await useAPI().get('admin/business-metrics', { params, timeout: 45_000, retry: 0 }) as BusinessMetrics
  },
  getAccessLog(opt: any) {
    return useAPI().post(`admin/get_access_log`, { body: opt }) as Promise<{accessCode: string}>
  },
  async sendPush(opt: any) {
    return ((await useAPI().post(`admin/send_push`, { body: opt })) as any).data as Promise<any>
  },
  async getPrismaMetrics(opt: any) {
    return (await useAPI().get(`admin/prisma_metrics`, { params: opt })).data
  },
  async getServiceTestResults(opt: any) {
    return await useAPI().post(`admin/get_service_test_results`, { body: opt }) as Promise<string | Array<any>>
  },
  getPMWTransactionsHTML(params: any) {
    return useAPI().get(`admin/pmw_transactions_html`, { params }) as Promise<string>
  },
  getPMWPayouts() {
    return useAPI().get(`admin/pmw_payouts`) as Promise<{
      rows: Array<{
        id: string
        date: string
        entity: string
        paidAmount: number
        bankFee: number
        currency: string
        status: string
        paymentMethod: string
      }>
      balances: {
        availableUSD: number
        pendingUSD: number
        rollingReserveUSD: number
      }
      lastCheckedAt: string
    }>
  },
  refreshPMWPayouts() {
    return useAPI().post(`admin/pmw_payouts/refresh`, { body: {} }) as Promise<{
      ok: boolean
      count: number
      balances: { availableUSD: number; pendingUSD: number; rollingReserveUSD: number }
    }>
  },
  refreshPMWBalances() {
    return useAPI().post(`admin/pmw_balances/refresh`, { body: {} }) as Promise<{
      ok: boolean
      balances: { availableUSD: number; pendingUSD: number; rollingReserveUSD: number }
      lastCheckedAt: string
    }>
  },
  // Warm the R2 cache for every payout missing a `details` record. Returns
  // an EventSource so the caller can render live progress.
  streamPMWPayoutsWarmCache() {
    return new EventSource('/api/admin/pmw_payouts/warm_cache_stream', { withCredentials: true })
  },
  // GET — R2-only. Throws if not yet cached (404).
  getPMWPayoutDetails(id: string) {
    return useAPI().get(`admin/pmw_payout_details/${id}`) as Promise<PmwPayoutDetailsResponse>
  },
  // SSE — open an EventSource that streams pages as they're fetched from
  // PaymentWall. Returns an object with the EventSource + a teardown helper.
  streamPMWPayoutDetails(id: string, opts: { force?: boolean } = {}) {
    const qs = opts.force ? '?force=1' : ''
    const url = `/api/admin/pmw_payout_details/${id}/stream${qs}`
    return new EventSource(url, { withCredentials: true })
  },
  async switchAutoChangePort(opt: any) {
    return await useAPI().post(`admin/auto_change_port`, { body: opt })
  },
  async getCurrentRelease() {
    const data = await useAPI().get(`funstuff/client_version`) as string
    return JSON.parse(data) as { stable:string, beta: string }
  },
  getUserClientHeartbeat({ userId, opt }: {userId: number; opt: any}) {
    return useAPI().get(`client/heart_beat/${userId}`, { params: opt })
  },
  // get({ versionStr, params }) {
  //   return useAPI().get(`client/update/${versionStr}`, { params })
  // },
  // add(body) {
  //   return useAPI().post(`client/update`, { body })
  // },
  // update(version) {
  //   return useAPI().put(`client/update/${version.version}`, { body: version })
  // },
  checkDeviceIdsBlocked(params) {
    return useAPI().get(`admin/blocked_device`, { params })
  },
  getBlockedDeviceBackOnlineRecords(params: {
    start?: string
    end?: string
    userId?: number | string
    newUserId?: number | string
    oldUserId?: number | string
    service?: string
    newServiceStatus?: string
    sameAccount?: number | string | boolean
    group?: 'none' | 'new' | 'old' | 'both' | string
    includeRecords?: number | string
    groupLimit?: number | string
    limit?: number | string
    offset?: number | string
  }) {
    return useAPI().get(`admin/blocked_device_back_online_records`, { params })
  },
  blockDevice(opt) {
    return useAPI().post(`admin/block_device`, { body: opt })
  },
  unblockDevice(opt: any) {
    return useAPI().post(`admin/unblock_device`, { body: opt })
  },
  getCryptoTransactions(params: any) {
    return useAPI().get(`crypto/transactions`, { params })
  },
  getCryptoPortfolio(params: any) {
    return useAPI().get(`crypto/portfolio`, { params })
  },
  switchProtocol(opt: {protocol: string, serviceId: number}) {
    return useAPI().post(`admin/disable_protocol`, { body: opt })
  },
  getPostmarkDetails(params: {messageIds: string[]}) {
    return useAPI().get(`postmark/message_details`, { params })
  },
  cacheOperation(commandArray: unknown[][]) {
    return useAPI().post(`admin/cache_operations`, {
      body: {
        commands: commandArray
      } 
    }) as Promise<CacheOperationGeneralResponse>
  },
  getDeviceIPHistory(userId, query) {
    return useAPI().post(`client/ip_history/${userId}`, { body: query })
  },
  getClientHeartBeatOfAppleAccount(appTransactionId: string) {
    return useAPI().get(`client/heart_beat_of_device`, {
      params: { [APPLE_ACCOUNT.heartbeatLookupParam]: appTransactionId },
    }) as Promise<any[]>
  },
  getAppStoreTransactionsOfAppleAccount(appTransactionId: string) {
    return useAPI().get(`admin/appstore/transactions`, {
      params: { [APPLE_ACCOUNT.transactionsLookupParam]: appTransactionId },
    }) as Promise<{ transactions: any[] }>
  },
  // Heartbeats, then purchases: one request at a time.
  async getRelatedUsersOfAppleAccount(appTransactionId: string) {
    const heartbeats = await useAdmin.getClientHeartBeatOfAppleAccount(appTransactionId)
    const purchases = await useAdmin.getAppStoreTransactionsOfAppleAccount(appTransactionId)
    return relatedAppleAccountUsers({ heartbeats, transactions: purchases.transactions })
  },
  checkAppleAccountsBlocked(appTransactionIds: string[]) {
    return useAPI().get(`admin/blocked_payer`, {
      // The server reads one comma-separated value.
      params: { [APPLE_ACCOUNT.blockedLookupParam]: appTransactionIds.join(',') },
    }) as Promise<Record<string, unknown>>
  },
  getClientHeartBeatOfDevice({ deviceUuid, lastSeenSince = null }) {
    return useAPI().get(`client/heart_beat_of_device`, {
      params: {
        device_uuid: deviceUuid,
        lastSeenSince
      }
    })
  },
  sendClientCommand(opt: {
    userId: number | string
    command: string
    payload?: any
    device_uuid?: string | null
    notify_on_ack?: boolean
    notify_on_result?: boolean
    notify_user_id?: number | string | null
    ttlSec?: number
  }) {
    return useAPI().post(`admin/client_command`, { body: opt })
  },
  getClientCommandResult(id: string) {
    return useAPI().get(`admin/client_command_result/${id}`)
  },
  getClientCommands(params: {
    id?: string
    user_id?: number | string
    command?: string
    status?: string
    device_uuid?: string
    issued_from?: string
    issued_to?: string
    limit?: number
    offset?: number
  }) {
    return useAPI().get(`admin/client_commands`, { params })
  },
  getClientCommandDetail(id: string) {
    return useAPI().get(`admin/client_commands/${id}`)
  },
  cancelClientCommands(ids: string[]) {
    return useAPI().post(`admin/client_commands/cancel`, { body: { ids } })
  },
  getAxiomConfig() {
    return useAPI().get(`admin/axiom_config`)
  },
  getBillOverview(params: {year?: number | string, month?: number | string}): PayAsYouGoBill[] {
    return useAPI().get(`admin/ali_bill`, { params })
  },
  getDynamicFeatures() {
    return useAPI().get(`admin/dynamic_features`) as Promise<{ [key: string]: any }>
  },
  getAutostatStatus() {
    return useAPI().get('admin/autostat') as Promise<AutostatStatus>
  },
  setAutostatConcurrency(concurrency: number | null, expectedConcurrency: number | null) {
    return useAPI().post('admin/autostat/concurrency', {
      body: { concurrency, expectedConcurrency },
    }) as Promise<Pick<AutostatStatus, 'configuredConcurrency' | 'effectiveConcurrency'>>
  },
  setDynamicFeatures(opt: {[key: string]: any}) {
    return useAPI().post(`admin/dynamic_features`, { body: opt }) as Promise<{ [key: string]: any }>
  },
  listDeploymentJobs(params?: { limit?: number }) {
    return useAPI().get(`admin/deployment-jobs`, { params })
  },
  createDeploymentJob(opt: {
    mode:
      | 'standard'
      | 'standard+xray'
      | 'standard+sudoku'
      | 'standard+hy2'
      | 'standard+naive'
      | 'standard+xray+sudoku'
      | 'standard+xray+hy2'
      | 'standard+xray+naive'
      | 'standard+sudoku+hy2'
      | 'standard+sudoku+naive'
      | 'standard+hy2+naive'
      | 'standard+xray+sudoku+hy2'
      | 'standard+xray+sudoku+naive'
      | 'standard+xray+hy2+naive'
      | 'standard+sudoku+hy2+naive'
      | 'standard+xray+sudoku+hy2+naive'
      | 'xray'
      | 'sudoku'
      | 'hy2'
      | 'naive'
      | 'gemini-egress'
      | `standard+${string}+gemini-egress`
      | 'standard+gemini-egress'
    serverName: string
    targetHost?: string
    notifyUser1?: boolean
    sshUser?: string
    sshPort?: number
    sshKey?: string
    dryRun?: boolean
    skipBase?: boolean
    skipSsl?: boolean
    services?: {
      v2ray?: boolean
      xray?: boolean
      trojan?: boolean
      squid?: boolean
      shadowsocks?: boolean
      wireguard?: boolean
      anyconnect?: boolean
    }
    ports?: {
      v2ray?: number
      xray?: number
      trojan?: number
      squid?: number
      shadowsocks?: number
      wireguard?: number
    }
  }) {
    return useAPI().post(`admin/deployment-jobs`, { body: opt })
  },
  getDeploymentJob(jobId: string, params?: { limit?: number }) {
    return useAPI().get(`admin/deployment-jobs/${jobId}`, { params })
  },
  cancelDeploymentJob(jobId: string) {
    return useAPI().post(`admin/deployment-jobs/${jobId}/cancel`, { body: {} })
  },
  getPlanPricing() {
    return useAPI().get('admin/plan-pricing') as Promise<PlanPricingResponse>
  },
  updatePlanPricing(opt: PlanPricingUpdateRequest) {
    return useAPI().put('admin/plan-pricing', { body: opt }) as Promise<PlanPricingResponse>
  },
  listPandaUiReleases(params?: { limit?: number }) {
    return useAPI().get(`admin/pandaui-releases`, { params })
  },
  rollbackPandaUiRelease(version: string, opt: {
    expectedCurrentActivationVersion?: string | null
  }) {
    return useAPI().post(
      `admin/pandaui-releases/${encodeURIComponent(version)}/rollback`,
      { body: opt },
    )
  },
  previewBigNewsCampaign(opt: BigNewsCampaignRequest) {
    return useAPI().post('admin/big-news/preview', { body: opt }) as Promise<BigNewsCampaignPreview>
  },
  publishBigNewsCampaign(opt: BigNewsCampaignRequest & { preview_signature: string }) {
    return useAPI().post('admin/big-news/publish', { body: opt })
  },
  listBigNewsCampaigns(params?: { limit?: number }) {
    return useAPI().get('admin/big-news', { params }) as Promise<any[]>
  },
  expireBigNewsCampaign(id: number) {
    return useAPI().put(`admin/big-news/${id}/expire`, { body: {} })
  },
}
